import { Stack, Typography } from '@montugroup/design-system';
import type { ConsultationResponseDto, ConsultationWaitlistDto } from '@montugroup/pms-api-contracts';

import { useWaitlistPreferenceForm } from '@/pages/consultations/hooks/useWaitlistPreferenceForm';
import { JoinButton } from './ConsultationWaitlistSection.styles';
import { isWaitlistRejoin, WAITLIST_JOINED_MESSAGE, waitlistJoinMessage } from './waitlist.config';
import { WaitlistPreferenceForm } from './WaitlistPreferenceForm';

export interface WaitlistJoinPromptProps {
  consultation: ConsultationResponseDto;
  waitlist: ConsultationWaitlistDto;
  /** called with the message to announce once the patient has joined */
  onOutcome: (message: string) => void;
  /** called when pms-service refuses the join; the section then explains it in place of this prompt */
  onRefused: () => void;
  /** called when the patient opens the form, which replaces an earlier refusal */
  onAction: () => void;
}

/**
 * Offers to join, or rejoin, the waitlist, and opens the preference form to do it.
 */
export function WaitlistJoinPrompt({
  consultation,
  waitlist,
  onOutcome,
  onRefused,
  onAction,
}: WaitlistJoinPromptProps) {
  const { isFormOpen, open, formProps, buttonFocusProps } = useWaitlistPreferenceForm({
    consultation,
    mode: 'join',
    successMessage: WAITLIST_JOINED_MESSAGE,
    onOutcome,
    onRefused,
    onAction,
  });

  if (isFormOpen) {
    return (
      <WaitlistPreferenceForm initialAvailability={waitlist.availability} submitLabel="Join waitlist" {...formProps} />
    );
  }

  const isRejoin = isWaitlistRejoin(waitlist);

  return (
    <Stack gap={2} alignItems="flex-start">
      <Typography variant="body1">{waitlistJoinMessage(isRejoin, consultation.practitioner?.name)}</Typography>
      <JoinButton type="button" variant="outlined" color="primary" {...buttonFocusProps} onClick={open}>
        {isRejoin ? 'Rejoin waitlist' : 'Join waitlist'}
      </JoinButton>
    </Stack>
  );
}
